import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import FieldContainer from "./components/FieldContainer"
import PropTypes from 'prop-types'
import styles from './Radio.module.scss'
import cls from 'classnames'
import Font from '@cred/react-font'
import RadioIcon from './components/Icon'
import { fieldContainerProps, fieldProps } from './commonProps'
import useInternalState from './components/useInternalState'
import withAnalytics from './components/withAnalytics'
import useAutoFocus from './components/useAutoFocus'
import { watchKeyboardBtnPress } from '@cred/shared/events'

const Radio = forwardRef(({
  id,
  error,
  warning,
  touched,
  focus,
  name,
  label,
  initialChecked,
  className,
  inputRef,
  inputProps,
  disabled,
  display,
  borderType,
  description,
  value,
  uncheckedIcon,
  checkedIcon,
  autoFocus,
  autoFocusDelay,
  checkedLabelPrefix,
  uncheckedLabelPrefix,
  appearance
}, ref) => {
  const managedInputRef = useRef(null),
    currentChecked = inputProps.checked !== undefined ? inputProps.checked : managedInputRef && managedInputRef.current && managedInputRef.current.checked,
    labelContainerClasses = {
      [styles.solidBorder]: borderType === 'solid',
      [styles.solidBorderCompact]: borderType === 'solid' && appearance === 'compact'
    },
    containerClasses = {
      [styles.displayGrouped]: display === 'grouped',
      [styles.displayGroupedCompact]: display === 'grouped' && appearance === 'compact',
      [styles.checkedGrouped]: display === 'grouped' && currentChecked
    },
    fontColor = disabled ? 'neutral400' : (display === 'grouped' && currentChecked) ? 'white' : 'neutral700',
    { internalFocus, internalTouched, internalInputProps } = useInternalState({ focus, touched, inputProps }),
    customRadioRef = useRef()

  useImperativeHandle(inputRef, () => managedInputRef.current)

  useAutoFocus(customRadioRef, autoFocus, autoFocusDelay)

  const isDisplayBlock = display === 'block'

  const showCheckTextPrefix = isDisplayBlock && checkedLabelPrefix && currentChecked
  const showUncheckLabelPrefix = isDisplayBlock && uncheckedLabelPrefix && !currentChecked

  return (
    <FieldContainer ref={ref} id={id} error={error} warning={warning} touched={internalTouched} focus={internalFocus} className={cls(className, styles.container, containerClasses)} appearance={appearance}>
      <input
        {...internalInputProps}
        type="radio"
        name={name}
        id={id}
        ref={managedInputRef}
        disabled={disabled}
        className={styles.input}
        value={value}
        defaultChecked={inputProps.checked === undefined ? initialChecked : undefined}
        data-testid="inputElement"
      />
      <span className={cls(styles.labelContainer, labelContainerClasses)} tabIndex={0} onKeyDown={watchKeyboardBtnPress()} ref={customRadioRef}>
        <div>
          <div className={cls({ semiBold: appearance !== 'compact' },fontColor)}>
            {showCheckTextPrefix && checkedLabelPrefix}
            {showUncheckLabelPrefix && uncheckedLabelPrefix}
            {label}
          </div>
          {isDisplayBlock && <div className={cls('bodySmall', { neutral400: disabled, neutral500: !disabled })}>{description}</div>}
        </div>
        {isDisplayBlock &&
          <div>
            {currentChecked && <RadioIcon icon={checkedIcon}/>}
            {!currentChecked && <RadioIcon icon={uncheckedIcon}/>}
          </div>
        }
      </span>
    </FieldContainer>
  )
})

Radio.propTypes = {
  ...fieldContainerProps,
  ...fieldProps,
  /** The label displayed to the user after the field has any value */
  label: PropTypes.node,
  /** The initial checked state of the input */
  initialChecked: PropTypes.bool,
  /** Sets the kind of display the component should have between grouped and block, grouped radios must be siblings to stick together */
  display: PropTypes.oneOf(['grouped', 'block']),
  /** The border type the component should have between noBorder or solid */
  borderType: PropTypes.oneOf(['noBorder', 'solid']),
  /** A description to be shown below the label (only for display block) */
  description: PropTypes.string,
  /** The value the radio button represents */
  value: PropTypes.any.isRequired,
  /** Icon to be displayed in to the right of the label while the field is unchecked */
  uncheckedIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Icon to be displayed in to the right of the label when the field is checked */
  checkedIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Icon to be displayed in to the left of the label when the field is checked */
  checkedLabelPrefix: PropTypes.node,
  /** Icon to be displayed in to the left of the label when the field is unchecked */
  uncheckedLabelPrefix: PropTypes.node,
  /** default: regular appearance used in prequal forms; compact: smaller inputs to fit smaller forms and widgets */
  appearance: PropTypes.oneOf(['default', 'compact'])
}

Radio.defaultProps = {
  error: null,
  warning: null,
  touched: null,
  label: null,
  initialChecked: false,
  focus: null,
  inputRef: null,
  inputProps: {},
  disabled: false,
  display: 'block',
  borderType: 'solid',
  description: null,
  uncheckedIcon: 'icon-chevron-right',
  checkedIcon: <Font element="i" type="medium" className={cls('cred-icon', 'icon-check-circle', styles.checkedIcon)}/>,
  autoFocus: false,
  autoFocusDelay: 500,
  checkedLabelPrefix: undefined,
  uncheckedLabelPrefix: undefined,
  appearance: 'default'
}

export default withAnalytics(Radio, {disabled: false, events: ['onChange']})
